import {
  useCallback,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  clearNativeGlassMotionRect,
  setNativeGlassMotionRect,
} from "./native-glass-motion";
import { requestNativeGlassRefresh } from "./native-glass-settings";
import { STANDALONE_BAR_INSET } from "@/components/panels/standalone-bar-placement";

const INTERACTIVE =
  'button,input,textarea,select,a,[role="button"],[role="textbox"],[role="menu"],[contenteditable]:not([contenteditable="false"])';
const DRAG_THRESHOLD = 4;
interface Pane {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
const clampPosition = (
  pane: Pane,
  x: number,
  y: number,
  width: number,
  height: number,
) => ({
  x: Math.max(
    pane.left + STANDALONE_BAR_INSET,
    Math.min(x, pane.right - width - STANDALONE_BAR_INSET),
  ),
  y: Math.max(
    pane.top + STANDALONE_BAR_INSET,
    Math.min(y, pane.bottom - height - STANDALONE_BAR_INSET),
  ),
});
const refreshGlass = () =>
  requestNativeGlassRefresh({
    forceSend: true,
    trackTransition: false,
  });

/** The dragged bar: its shell, and the field a press may drag while empty. */
export interface DragBarParts {
  shell: string;
  field: string;
}

const STANDALONE_BAR_PARTS: DragBarParts = {
  shell: '[data-testid="standalone-bar"]',
  field: '[data-testid="standalone-bar-field"]',
};

/** Whether a press on the field may start a drag: it holds no draft yet. */
function isEmptyField(field: HTMLElement) {
  if (field instanceof HTMLTextAreaElement) return field.value === "";
  return (
    field.textContent === "" && !field.querySelector("[data-standalone-chip]")
  );
}

/** The shell's rect where a drag finished, in client pixels. */
interface StandaloneInputDrop {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Drags the standalone composer (or another floating input bar, named by
 * `parts`) by translating its wrapper, with the native glass following the
 * moved shell. A press anywhere on the bar but its controls drags it, the
 * field too while it is empty.
 *
 * Without `onDrop` the translate keeps the dragged position (the shortcut
 * bar, which stays inside the window). With `onDrop` the caller owns
 * placement: the bar stays inside the wrapper's pane, and each drop hands the
 * dropped shell rect back so the caller re-anchors the bar there while the
 * translate is dropped.
 */
export function useStandaloneInputDrag(
  summonKey: string | number | null,
  enabled: boolean,
  onDrop?: (drop: StandaloneInputDrop) => void,
  parts: DragBarParts = STANDALONE_BAR_PARTS,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const offset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  const stopRef = useRef<(() => void) | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  // The untranslated box the bar must stay inside.
  const paneOf = useCallback((container: HTMLElement): Pane => {
    if (!onDropRef.current) {
      return {
        left: 0,
        top: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      };
    }
    const rect = container.getBoundingClientRect();
    return {
      left: rect.left - offset.current.x,
      top: rect.top - offset.current.y,
      right: rect.right - offset.current.x,
      bottom: rect.bottom - offset.current.y,
    };
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    offset.current = { x: 0, y: 0 };
    hasMoved.current = false;
    if (container) container.style.translate = "";
    return () => {
      stopRef.current?.();
    };
  }, [summonKey]);

  useLayoutEffect(() => {
    if (!enabled) stopRef.current?.();
    const container = containerRef.current;
    const shell = container?.querySelector<HTMLElement>(parts.shell);
    if (!enabled || !container || !shell) return;
    let resizeFrame = 0;
    const keepOnScreen = () => {
      if (!hasMoved.current) return;
      const rect = shell.getBoundingClientRect();
      const { x, y } = clampPosition(
        paneOf(container),
        rect.left,
        rect.top,
        rect.width,
        rect.height,
      );
      const dx = x - rect.left;
      const dy = y - rect.top;
      if (dx === 0 && dy === 0) return;
      offset.current = { x: offset.current.x + dx, y: offset.current.y + dy };
      container.style.translate = `${offset.current.x}px ${offset.current.y}px`;
      // The translated DOM rect now owns placement, including during growth.
      clearNativeGlassMotionRect(shell);
      refreshGlass();
    };
    const onWindowResize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(keepOnScreen);
    };
    const observer = new ResizeObserver(keepOnScreen);
    observer.observe(shell);
    window.addEventListener("resize", onWindowResize);
    keepOnScreen();
    return () => {
      stopRef.current?.();
      observer.disconnect();
      cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", onWindowResize);
    };
  }, [enabled, paneOf, parts.shell, summonKey]);

  const onPointerDownCapture = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled || event.button !== 0 || stopRef.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const editor = target.closest<HTMLElement>(parts.field);
      const emptyEditor = Boolean(editor && isEmptyField(editor));
      const interactive = target.closest(INTERACTIVE);
      if (interactive && !(emptyEditor && interactive === editor)) return;
      const shell = target.closest<HTMLElement>(parts.shell);
      const container = event.currentTarget;
      if (!shell || !container.contains(shell)) return;

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      const initialOffset = { ...offset.current };
      const initialRect = shell.getBoundingClientRect();
      const pane = paneOf(container);
      let dragging = false;

      const stop = () => {
        stopRef.current = null;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
        window.removeEventListener("blur", finish);
        container.removeEventListener("lostpointercapture", end);
        if (container.hasPointerCapture?.(pointerId))
          container.releasePointerCapture(pointerId);
        clearNativeGlassMotionRect(shell);
        if (dragging) refreshGlass();
      };
      const finish = () => {
        const commit = dragging ? onDropRef.current : undefined;
        stop();
        if (!commit) return;
        // The caller's re-anchored left/top replace the translate before the
        // next paint, so the bar stays where it was dropped.
        const rect = shell.getBoundingClientRect();
        const drop = {
          left: initialRect.left + offset.current.x - initialOffset.x,
          top: initialRect.top + offset.current.y - initialOffset.y,
          width: rect.width,
          height: rect.height,
        };
        offset.current = { x: 0, y: 0 };
        hasMoved.current = false;
        container.style.translate = "";
        commit(drop);
      };
      const end = (next: PointerEvent) => {
        if (next.pointerId === pointerId) finish();
      };
      const move = (next: PointerEvent) => {
        if (next.pointerId !== pointerId) return;
        const dx = next.clientX - startX;
        const dy = next.clientY - startY;
        if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        if (!dragging) {
          dragging = true;
          hasMoved.current = true;
          try {
            container.setPointerCapture(pointerId);
          } catch {
            // Window listeners also finish drags when capture is unavailable.
          }
        }
        const rect = shell.getBoundingClientRect();
        const { x, y } = clampPosition(
          pane,
          initialRect.left + dx,
          initialRect.top + dy,
          rect.width,
          rect.height,
        );
        offset.current = {
          x: initialOffset.x + x - initialRect.left,
          y: initialOffset.y + y - initialRect.top,
        };
        container.style.translate = `${offset.current.x}px ${offset.current.y}px`;
        setNativeGlassMotionRect(shell, {
          x,
          y,
          width: rect.width,
          height: rect.height,
        });
        refreshGlass();
        next.preventDefault();
      };

      stopRef.current = stop;
      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", end);
      window.addEventListener("pointercancel", end);
      window.addEventListener("blur", finish);
      container.addEventListener("lostpointercapture", end);
      // A click in an empty editor must still place focus. Once it contains a
      // draft, ordinary pointer selection takes precedence over dragging.
      if (!emptyEditor) event.preventDefault();
    },
    [enabled, paneOf, parts.field, parts.shell],
  );

  return { containerRef, dragHandlers: { onPointerDownCapture } };
}
